import { FloatingFormItem } from '@/components/_common';
import { Col, Form, Input, Modal, ModalProps, Radio, Row } from 'antd';
import { ColorResult } from '@uiw/color-convert';
import Circle from '@uiw/react-color-circle';
import { useDefaultEmptyFieldError } from '@/hooks';

import { useEffect } from 'react';
import { WorkOrderStatusFormValues } from '@/core/domain/workorder/WorkOrderStatus/WorkOrderStatus.types';
import {
  useGPStatusCreate,
  useGPStatusEdit,
} from '@/core/domain/visual-management/status/status.hooks';
import RadioGroup from '@/components/_common/RadioGroup';
import {
  GPStatus,
  GPStatusColor,
  GPStatusColorRequest,
} from '@/core/domain/visual-management/status/status.types';

interface Props extends Omit<ModalProps, 'onCancel'> {
  statusSelected?: GPStatus;
  onCancel: VoidFunction;
  companyId?: number;
  completeActivity?: boolean;
}

export default function StatusWorkOrderModal({
  statusSelected,
  onCancel,
  completeActivity,
  ...props
}: Props) {
  const [form] = Form.useForm<WorkOrderStatusFormValues>();
  const defaultEmptyError = useDefaultEmptyFieldError();

  const editStatus = useGPStatusEdit();
  const createStatus = useGPStatusCreate();

  useEffect(() => {
    if (statusSelected) {
      form.setFieldsValue({
        ...statusSelected,
        type: String(statusSelected.type),
        color: GPStatusColor[statusSelected.color],
      });
    }
  }, [statusSelected, props.open]);

  async function handleOk() {
    const values = await form.validateFields();

    if (statusSelected?.id) {
      await editStatus.mutateAsync({
        id: statusSelected.id,
        ...values,
        type: Number(values.type),
        color: GPStatusColorRequest[values.color.toUpperCase()],
      });
    } else {
      await createStatus.mutateAsync({
        ...values,
        type: Number(values.type),
        color: GPStatusColorRequest[values.color.toUpperCase()],
      });
    }

    handleCancel();
  }

  function handleCancel() {
    form.resetFields();
    onCancel();
  }

  useEffect(() => {
    if (props.open && completeActivity && !statusSelected) {
      form.setFieldsValue({
        color: '#51CF66',
        name: 'Concluído',
        type: '3',
      });
    }
  }, [props.open, completeActivity, statusSelected]);

  return (
    <Modal
      {...props}
      title={`${!statusSelected?.id ? 'Criação ' : 'Edição '}do status`}
      width={500}
      okText={!statusSelected?.id ? 'Criar' : 'Editar'}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={createStatus.isLoading || editStatus.isLoading}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <FloatingFormItem
              name="name"
              label="Descrição"
              rules={[{ ...defaultEmptyError, whitespace: true }]}
            >
              <Input maxLength={50} />
            </FloatingFormItem>
          </Col>

          <Form.Item name="type" label="Tipo de status" initialValue="0">
            <RadioGroup style={{ marginBottom: '8px' }}>
              <Radio value="0" disabled={completeActivity}>
                Abertura
              </Radio>
              <Radio value="1" disabled={completeActivity}>
                Progresso
              </Radio>
              <Radio value="2" disabled={completeActivity}>
                Finalização
              </Radio>
              <Radio value="3">Concluído</Radio>
            </RadioGroup>
          </Form.Item>

          <Form.Item
            name="color"
            label="Cor"
            getValueFromEvent={(e: ColorResult) => {
              return e.hex;
            }}
            rules={[defaultEmptyError]}
            valuePropName="color"
          >
            <Circle
              colors={[
                '#3B3B3B',
                '#ADB5BD',
                '#FF6B6B',
                '#F06595',
                '#CC5DE8',
                '#845EF7',
                '#5C7CFA',
                '#339AF0',
                '#22B8CF',
                '#20C997',
                '#51CF66',
                '#94D82D',
                '#FCC419',
                '#FF922B',
                '#6C76A7',
              ]}
            />
          </Form.Item>
        </Row>
      </Form>
    </Modal>
  );
}
